/**
 * The public pricing page's one call.
 *
 * **Anonymous**, so it bypasses the shared api client exactly as the signup lookup does: the page is
 * for a coach with no account, and a signed-in visitor's token has no business on a request for
 * global product data. It also keeps `api.ts`'s 401 and awaiting-payment handling, which would sign
 * someone out or redirect them, away from a page anyone can open.
 *
 * **The body is read through guards, not cast.** Web and the API deploy separately, so this build
 * can meet a server that does not have the endpoint yet (a 404, shown as the load error) or one
 * that sends a field differently. A tier it cannot read, or one with no price, is dropped rather
 * than shown as "$0 / year". `sportPrices` and `trialDays` are newer fields: when absent the tier
 * shows its single legacy price and the page says nothing about a trial. `planTiers` and
 * `planFeatures` (baseball's plans) are newer still: absent, both read as empty lists and the page
 * renders exactly as it did before plans.
 */

import { parsePlanFeatures, parsePlanTierOffers, plainDate, positivePercent } from './planTierOffers';

import type {
  PublicPricingResponse,
  PublicPricingSport,
  PublicPricingTier,
  PublicSportPrice,
} from '../types/pricing';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5206';
const PRICING_URL = `${API_BASE_URL}/api/public/pricing`;

const DEFAULT_CURRENCY = 'usd';

export const PRICING_LOAD_MESSAGE = 'We could not load our prices. Check your connection and try again.';
export const PRICING_RATE_LIMITED_MESSAGE = 'Too many requests. Wait a minute and try again.';

/** A failed pricing load. `message` is always displayable. */
export class PricingError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'PricingError';
    this.status = status;
  }
}

type Rec = Record<string, unknown>;

const asRecord = (value: unknown): Rec =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Rec) : {};

const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

const num = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const SPORT_ORDER: readonly PublicPricingSport[] = ['baseball', 'softball'];

/** Known sports with a price above zero, one each, baseball first. Anything else is dropped. */
const toSportPrices = (value: unknown): PublicSportPrice[] => {
  if (!Array.isArray(value)) return [];
  const bySport = new Map<PublicPricingSport, number>();
  for (const item of value) {
    const row = asRecord(item);
    const sport = row.sport === 'baseball' || row.sport === 'softball' ? row.sport : null;
    const price = num(row.annualPriceCents);
    if (!sport || price === null || price <= 0 || bySport.has(sport)) continue;
    bySport.set(sport, Math.round(price));
  }
  return SPORT_ORDER.flatMap((sport) => {
    const annualPriceCents = bySport.get(sport);
    return annualPriceCents === undefined ? [] : [{ sport, annualPriceCents }];
  });
};

const toTier = (value: unknown): PublicPricingTier | null => {
  const row = asRecord(value);
  const key = text(row.key);
  if (!key) return null;
  const sportPrices = toSportPrices(row.sportPrices);
  const legacy = num(row.annualPriceCents);
  // The legacy field is the first sport's price; fall back to it the other way round too.
  const price = legacy !== null && legacy > 0 ? Math.round(legacy) : (sportPrices[0]?.annualPriceCents ?? 0);
  if (price <= 0) return null;
  return {
    key,
    name: text(row.name) ?? key,
    description: text(row.description),
    annualPriceCents: price,
    sportPrices,
    sortOrder: num(row.sortOrder) ?? 0,
  };
};

/** A whole number of days above zero, or null (an old API, or nothing usable). */
const toTrialDays = (value: unknown): number | null => {
  const days = num(value);
  return days !== null && days > 0 ? Math.floor(days) : null;
};

/** The server already orders them; sorting again keeps the order right if it ever does not. */
const byOrderThenName = (a: PublicPricingTier, b: PublicPricingTier): number =>
  a.sortOrder - b.sortOrder || a.name.localeCompare(b.name);

export const parsePricingResponse = (value: unknown): PublicPricingResponse => {
  const body = asRecord(value);
  const seen = new Set<string>();
  const tiers = (Array.isArray(body.tiers) ? body.tiers : [])
    .map(toTier)
    .filter((tier): tier is PublicPricingTier => {
      if (!tier || seen.has(tier.key)) return false;
      seen.add(tier.key);
      return true;
    })
    .sort(byOrderThenName);

  const threeYearDiscount = positivePercent(body.threeYearDiscountPercent);
  const threeYearOfferEndsOn = threeYearDiscount !== null ? plainDate(body.threeYearOfferEndsOn) : null;
  return {
    currency: text(body.currency)?.toLowerCase() ?? DEFAULT_CURRENCY,
    // Only an explicit true sends a coach to signup: a body that does not say must not lead them
    // into a checkout that may refuse them.
    signupAvailable: body.signupAvailable === true,
    tiers,
    trialDays: toTrialDays(body.trialDays),
    planTiers: parsePlanTierOffers(body.planTiers),
    planFeatures: parsePlanFeatures(body.planFeatures),
    // Only carried when offered, so a response from an older API reads exactly as before.
    ...(threeYearDiscount !== null ? { threeYearDiscountPercent: threeYearDiscount } : {}),
    ...(threeYearOfferEndsOn !== null ? { threeYearOfferEndsOn } : {}),
  };
};

/** The tiers on sale and whether online signup is open. */
export const getPublicPricing = async (signal?: AbortSignal): Promise<PublicPricingResponse> => {
  let response: Response;
  try {
    response = await fetch(PRICING_URL, { method: 'GET', headers: { Accept: 'application/json' }, signal });
  } catch (error) {
    // An abort is the caller's own doing; let it through so the caller can ignore it.
    if (signal?.aborted) throw error;
    throw new PricingError(PRICING_LOAD_MESSAGE, 0);
  }

  if (!response.ok) {
    throw new PricingError(
      response.status === 429 ? PRICING_RATE_LIMITED_MESSAGE : PRICING_LOAD_MESSAGE,
      response.status
    );
  }

  const body: unknown = await response.json().catch(() => null);
  if (body === null || typeof body !== 'object' || Array.isArray(body)) {
    throw new PricingError(PRICING_LOAD_MESSAGE, response.status);
  }
  return parsePricingResponse(body);
};
